import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cpu, Search, Heart, ShieldCheck, ArrowRight } from 'lucide-react';

const iconComponents = [Cpu, Search, Heart, ShieldCheck];

const ServiceCard = ({ service, index, isVisible, t, isRTL }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const IconComponent = iconComponents[index];

  return (
    <div
      className={`group relative bg-white/[0.02] border border-white/10 p-8 transition-all duration-500 hover:border-[#00FFD1]/40 hover-glow opacity-0 ${
        isVisible ? `animate-fade-in-up delay-${(index + 1) * 100}` : ''
      }`}
    >
      {/* Service Number */}
      <div className={`absolute top-6 ${isRTL ? 'left-6' : 'right-6'} text-6xl font-bold text-white/5 select-none`}>
        0{index + 1}
      </div>

      {/* Icon */}
      <div className="w-14 h-14 flex items-center justify-center bg-[#00FFD1]/10 mb-6 group-hover:bg-[#00FFD1]/20 transition-colors duration-500">
        <IconComponent className="w-7 h-7 text-[#00FFD1]" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-[#00FFD1] transition-colors duration-300">
        {service.title}
      </h3>

      {/* Problem */}
      <div className="mb-4">
        <span className="text-xs uppercase tracking-wider text-white/40 mb-2 block">{t('services.problem')}</span>
        <p className="text-white/60 text-sm leading-relaxed">{service.problem}</p>
      </div>

      {/* Expandable Content */}
      <div
        className={`overflow-hidden transition-all duration-500 ${
          isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {/* Solution */}
        <div className="mb-4 pt-4 border-t border-white/5">
          <span className="text-xs uppercase tracking-wider text-[#00FFD1]/60 mb-2 block">{t('services.solution')}</span>
          <p className="text-white/60 text-sm leading-relaxed">{service.solution}</p>
        </div>

        {/* Outcome */}
        <div className="mb-4">
          <span className="text-xs uppercase tracking-wider text-[#00FFD1]/60 mb-2 block">{t('services.outcome')}</span>
          <p className="text-white/60 text-sm leading-relaxed">{service.outcome}</p>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-2 mt-4">
          {service.features.map((feature, idx) => (
            <span
              key={feature}
              className="text-xs px-3 py-1 bg-white/5 text-white/70 border border-white/10"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>

      {/* Expand Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-6 flex items-center gap-2 text-sm text-[#00FFD1] hover:text-white transition-colors duration-300"
      >
        <span>{isExpanded ? t('services.showLess') : t('services.learnMore')}</span>
        <ArrowRight
          className={`w-4 h-4 transition-transform duration-300 ${
            isExpanded ? 'rotate-90' : ''
          } ${isRTL ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Bottom Accent Line */}
      <div className={`absolute bottom-0 ${isRTL ? 'right-0' : 'left-0'} w-0 h-[2px] bg-[#00FFD1] group-hover:w-full transition-all duration-700`} />
    </div>
  );
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isRTL = i18n.language === 'ar';
  const services = t('services.items', { returnObjects: true });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#080808]"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
              isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
              {t('services.title')}
            </span>
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
          </div>

          <h2
            className={`display-large text-white mb-4 opacity-0 ${
              isVisible ? 'animate-fade-in-up delay-100' : ''
            }`}
          >
            {t('services.subtitle')}
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              isVisible={isVisible}
              t={t}
              isRTL={isRTL}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
